import { cn } from '@/lib/utils';
import { Check, Compass, Search, Wrench } from 'lucide-react';
import { PropsWithChildren, useMemo } from 'react';

import type { IStep } from '@chainlit/react-client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { BrandLogo } from '@/components/BrandLogo';
import { Translator } from 'components/i18n';

interface Props {
  step: IStep;
  isRunning?: boolean;
}

const TOOL_ACTIVITY_COPY: Record<
  string,
  { active: string; complete: string; Icon: typeof Search }
> = {
  'Searching the IPMA website': {
    active: 'Searching the IPMA website',
    complete: 'Searched the IPMA website',
    Icon: Search
  },
  'Preparing IPMA page guidance': {
    active: 'Preparing page guidance',
    complete: 'Prepared page guidance',
    Icon: Compass
  }
};

export const getToolActivityCopy = (name: string) => {
  const knownTool = TOOL_ACTIVITY_COPY[name];

  if (knownTool) return knownTool;

  return {
    active: `Using ${name.replace(/[_-]+/g, ' ')}`,
    complete: `Used ${name.replace(/[_-]+/g, ' ')}`,
    Icon: Wrench
  };
};

interface ToolActivityGroupProps {
  steps: IStep[];
  isRunning?: boolean;
}

export function ToolActivityGroup({
  steps,
  isRunning
}: ToolActivityGroupProps) {
  if (!steps.length) return null;

  return (
    <section
      aria-label="IPMA Copilot activity"
      className="tool-activity-group my-2 flex items-start gap-2.5"
      aria-live="polite"
    >
      <BrandLogo
        size="avatar"
        variant="mark"
        className="mt-0.5 shrink-0"
      />
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 pt-1.5">
        {steps.map((step) => {
          const { active, complete, Icon } = getToolActivityCopy(step.name);
          const using = Boolean(
            isRunning && step.start && !step.end && !step.isError
          );
          const label = step.isError
            ? `${active} could not be completed`
            : using
              ? active
              : complete;

          return (
            <span
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground"
              key={step.id}
            >
              <Icon
                aria-hidden="true"
                className={cn(
                  'size-3.5 shrink-0 stroke-[1.8]',
                  step.isError && 'text-destructive',
                  using && 'text-primary'
                )}
              />
              <span className={cn(using && 'copilot-tool-activity-running')}>
                {label}
                {using ? <span aria-hidden="true">…</span> : null}
              </span>
              {!using && !step.isError ? (
                <Check
                  aria-label="Completed"
                  className="size-3.5 shrink-0 text-muted-foreground/65 stroke-[2]"
                />
              ) : null}
            </span>
          );
        })}
      </div>
    </section>
  );
}

export default function Step({
  step,
  children,
  isRunning
}: PropsWithChildren<Props>) {
  const using = useMemo(() => {
    return isRunning && step.start && !step.end && !step.isError;
  }, [step, isRunning]);

  const hasContent = step.input || step.output || step.steps?.length;
  const isError = step.isError;
  const stepName = step.name;

  // Tool calls are normally grouped by Messages. Keep this fallback quiet in
  // case a custom Chainlit integration renders an isolated tool step.
  if (step.type === 'tool') {
    return <ToolActivityGroup steps={[step]} isRunning={isRunning} />;
  }

  // If there's no content, just render the status without accordion
  if (!hasContent) {
    return (
      <div className="flex flex-col flex-grow w-0">
        <p
          className={cn(
            'flex items-center gap-1.5 text-xs font-medium',
            isError && 'text-red-500',
            !using && 'text-muted-foreground',
            using && 'loading-shimmer'
          )}
          id={`step-${stepName}`}
        >
          {using ? (
            <>
              <Translator path="chat.messages.status.using" /> {stepName}
            </>
          ) : (
            <>
              <Translator path="chat.messages.status.used" /> {stepName}
            </>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-grow w-0">
      <Accordion
        type="single"
        collapsible
        defaultValue={step.defaultOpen ? step.id : undefined}
        className="w-full rounded-xl border border-border/60 bg-muted/30 px-3 py-2"
      >
        <AccordionItem value={step.id} className="border-none">
          <AccordionTrigger
            className={cn(
              'flex items-center justify-start gap-1.5 p-0 text-xs font-medium transition-colors hover:no-underline',
              isError && 'text-red-500',
              !using && 'text-muted-foreground hover:text-foreground',
              using && 'loading-shimmer'
            )}
            id={`step-${stepName}`}
          >
            {using ? (
              <>
                <Translator path="chat.messages.status.using" /> {stepName}
              </>
            ) : (
              <>
                <Translator path="chat.messages.status.used" /> {stepName}
              </>
            )}
          </AccordionTrigger>
          <AccordionContent>
            <div className="ml-1 mt-3 flex-grow border-l-2 border-primary/40 pl-4">
              {children}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
